import type { UseQueryOptions } from "@tanstack/react-query";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";

import type { ListParams } from "app/pages/sales-intelligence/domain/lists/types/list";
import type {
    AppsSearchRequestDto,
    AppsSearchResponseDto,
} from "app/pages/sales-intelligence/modules/apps-search/types/dto";
import { withDemoMode } from "app/pages/sales-intelligence/modules/leads-search/query/options";
import type {
    LeadsSearchRequestDto,
    LeadsSearchResponseDto,
    LeadsType,
    SearchDemoMode,
} from "app/pages/sales-intelligence/modules/leads-search/types";
import { opportunityListQueryKeyFactory } from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import { assertSearchResults, searchResultsQueryDefaults } from "app/pages/sales-intelligence/query/options";
import {
    retryDelayForSearchResults,
    retrySearchResultsQuery,
    throwOnUnhandledSearchError,
} from "app/pages/sales-intelligence/query/utils";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { assertDefined } from "app/pages/sales-intelligence/utils/errors/assertions";
import { errorOperationMap } from "app/pages/sales-intelligence/utils/errors/context";

export const createAppsSearchResultsQuery = <T = AppsSearchResponseDto>(
    dto: AppsSearchRequestDto,
    listParams: ListParams,
    isLeadsSearchDtoReady: boolean,
    select?: (data: AppsSearchResponseDto) => T,
) =>
    queryOptions<AppsSearchResponseDto, unknown, T>({
        enabled: isLeadsSearchDtoReady,
        staleTime: Number.POSITIVE_INFINITY,
        queryKey: opportunityListQueryKeyFactory.appsTableData(listParams.id, dto),
        placeholderData: keepPreviousData,
        select,
        throwOnError: throwOnUnhandledSearchError,
        retry: retrySearchResultsQuery,
        retryDelay: retryDelayForSearchResults,
        queryFn: async ({ signal }) => {
            const response = await salesApiService.opportunityListPage.fetchAppsSearchResults(
                { listParams, dto },
                { cancellation: signal },
            );

            assertDefined(response, {
                name: "fetchSearchResultsResponse",
                operation: errorOperationMap.emptySearchResults,
            });

            return response;
        },
    });

type LeadsSearchResultsQueryParams<T> = {
    readonly leadsType: LeadsType;
    readonly dto: LeadsSearchRequestDto;
    readonly listParams: ListParams;
    readonly enabled: boolean;
    /**
     * Passed through by reference — never wrap or default it. React-query memoizes select results by function
     * identity, so any wrapper here re-runs the row transform on every render.
     */
    readonly select?: (data: LeadsSearchResponseDto) => T;
    /**
     * Defaults to surfacing unhandled search errors. The fan-out fetcher passes `false`: its reader twin runs
     * with `enabled: false` on the same key and is the observer that reports the error to the boundary.
     */
    readonly throwOnError?: UseQueryOptions<LeadsSearchResponseDto, unknown, T>["throwOnError"];
    /** Active demo preview, from `useSearchDemoMode`. Temporary — remove with the demo feature. */
    readonly demoMode?: SearchDemoMode;
};

/**
 * The single definition of the opportunity-list leads results query. Owning the `queryKey` and the `queryFn`
 * together is the point: the fan-out fetcher and its `enabled: false` reader twin observe the same cache
 * entry, so their keys cannot drift apart — including once the demo marker is stamped on, which happens once
 * here so the key and the request body can never disagree about it.
 */
export const createLeadsSearchResultsQuery = <T = LeadsSearchResponseDto>({
    leadsType,
    dto,
    listParams,
    enabled,
    select,
    throwOnError = throwOnUnhandledSearchError,
    demoMode,
}: LeadsSearchResultsQueryParams<T>) => {
    const requestDto = withDemoMode(dto, demoMode);

    return queryOptions<LeadsSearchResponseDto, unknown, T>({
        ...searchResultsQueryDefaults,
        enabled,
        select,
        throwOnError,
        queryKey: opportunityListQueryKeyFactory.leadsTableData(listParams.id, leadsType, requestDto),
        queryFn: async ({ signal }) => {
            const response = await salesApiService.opportunityListPage.fetchLeadsSearchResults(
                { listParams, leadsType, dto: requestDto },
                { cancellation: signal },
            );

            assertSearchResults(response);

            return response;
        },
    });
};
